import { BulkClassSessionDayScheduleSchema } from "@/modules/classes/domain/contracts/sessions";
import { offSiteDistanceSchema } from "@/modules/private-lessons/domain/lib/off-site-distance";
import {
  PartnerCreditInstallmentSchema,
  PartnerPricingStatusSchema,
} from "./partner-project-pricing";

export { PartnerCreditInstallmentSchema };
import {
  formatInstallmentTotalMismatchMessage,
  sumInstallmentAmounts,
  validateInstallmentPlanTotal,
} from "@/shared/lib/credit-invoice-installments";
import { parseTimeToDate } from "@/shared/lib/date-time";
import { z } from "zod";

const dateYmdRegex = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "ກະລຸນາກອກວັນທີໃຫ້ຖືກຮູບແບບ ປີ-ເດືອນ-ວັນ");

/** ຕາຕະລາງອັດຕະໂນມັດຕໍ່ 1 ຄລາດສ໌ — ສ້າງ session ເປັນ off_site + partner_instructor_only */
export const SellPartnerPerClassScheduleSchema = z
  .object({
    templateId: z.string().min(1, "ແມ່ແບບບໍ່ຖືກ"),
    instructorIds: z
      .array(z.string().min(1))
      .min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຜູ້ສອນສຳລັບຄລາດສ໌ນີ້"),
    capacity: z.coerce
      .number()
      .int("ຄວາມຈຸຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຄວາມຈຸຕ້ອງເປັນຈຳນວນບວກ"),
    notes: z.string().max(2000).nullable().optional(),
    daySchedules: z
      .array(BulkClassSessionDayScheduleSchema)
      .min(1, "ຕ້ອງເລືອກວັນໃນອາທິດ ຢ່າງນ້ອຍ 1 ວັນ ແລະ ກຳນົດເວລາໃຫ້ທົກວັນທີ່ເປີດໃຊ້"),
  })
  .refine(
    (d) =>
      d.daySchedules.every(
        (s) => s.roomId == null || String(s.roomId).trim().length === 0,
      ),
    {
      message: "ຄລາດສ໌ partner ຕາກຳນົດນອກສະຖານທີ່ — ບໍ່ກຳນົດຫ້ອງສະຕູດິໂອໃນຕາຕະລາງ",
      path: ["daySchedules"],
    },
  )
  .refine(
    (d) => {
      for (const s of d.daySchedules) {
        const start = parseTimeToDate(s.startTime);
        const end = parseTimeToDate(s.endTime);
        if (end <= start) return false;
      }
      return true;
    },
    {
      message: "ເວລາສິ້ນສຸດຕ້ອງຫຼາຍກວ່າເວລາເລີ່ມຕົ້ນ",
      path: ["daySchedules"],
    },
  );

/** Object-only schema — ຟອມ .pick ຕ້ອງສະເພາະອັນນີ້ (ບໍ່ນຳ schema ທີ່ມີ refine ຫຼາຍເທົ່າກັ້ນເອົາມາຊ້ອນເພາະຈະກິດ Zod pick) */
export const SellPartnerProjectObjectSchema = z.object({
  classTemplateIds: z
    .array(z.string().min(1))
    .min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ແມ່ແບບຄລາດສ໌"),
  /** draft = ຍັງບໍ່ກຳນົດ · quoted = ໃສ່ລາຄາແລ້ວ · locked = ເກັບເງິນ */
  pricingStatus: PartnerPricingStatusSchema.optional().default("locked"),
  /** ຍອດປະມານ (draft) */
  provisionalTotal: z.coerce.number().int().min(0).nullable().optional(),
  /** ຍອດຕາມໂຄງການກ່ອນຫຼຸດ — ບໍ່ບັງຄັບເມື່ອ draft */
  price: z.coerce.number().int().min(0).optional(),
  partnerLabel: z.string().max(300).optional().nullable(),
  /** ໄລຍະນອກສະຖານທີ່ — partner ອອກສະຖານທີ່ຕ້ອງມີ (default ໃກ້) */
  offSiteDistance: offSiteDistanceSchema.default("near"),
  notes: z.string().max(2000).optional().nullable(),
  startDate: dateYmdRegex,
  expireDate: dateYmdRegex,
  discountType: z.enum(["percentage", "fixed"]).optional(),
  discountValue: z.number().optional(),
  settlementMode: z.enum(["receipt", "credit"]).optional().default("receipt"),
  amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
  /** จำนวนงวดชำระ (โหมด credit) — 2–12 */
  installmentCount: z.coerce.number().int().min(2).max(12).optional(),
  /** แผนงวดชำระ (โหมด credit) — ยอดรวมต้องเท่า finalPrice */
  installments: z.array(PartnerCreditInstallmentSchema).optional(),
  schedules: z
    .array(SellPartnerPerClassScheduleSchema)
    .min(1, "ຕ້ອງກຳນົດຕາຕະລາງຄບກັບ ຄລາດສ໌ ທີ່ເລືອກ"),
});

export const SellPartnerProjectSchema = SellPartnerProjectObjectSchema.refine(
  ({ startDate, expireDate }) => expireDate >= startDate,
  {
    message: "ວັນໝົດອາຍຸຕ້ອງເທົ່າກັນ ຫຼື ຫຼັງວັນເລີ່ມ",
    path: ["expireDate"],
  },
)
  .refine(
    (data) => {
      if (data.discountType && data.discountValue === undefined) return false;
      if (data.discountValue !== undefined && !data.discountType) return false;
      if (data.discountType && data.discountValue !== undefined) {
        if (data.discountType === "percentage") {
          return data.discountValue >= 0 && data.discountValue <= 100;
        }
        if (data.discountType === "fixed") {
          return data.discountValue > 0;
        }
      }
      return true;
    },
    {
      message: "ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
      path: ["discountValue"],
    },
  )
  .refine(
    (data) => {
      const uniqueTpl = [...new Set(data.classTemplateIds)];
      if (uniqueTpl.length !== data.classTemplateIds.length) return false;
      if (uniqueTpl.length !== data.schedules.length) return false;
      const schedTpl = data.schedules.map((s) => s.templateId);
      const uniqSched = [...new Set(schedTpl)];
      if (uniqSched.length !== schedTpl.length) return false;
      const setTpl = new Set(uniqueTpl);
      const setSch = new Set(schedTpl);
      if (setTpl.size !== setSch.size) return false;
      for (const id of uniqueTpl) if (!setSch.has(id)) return false;
      for (const id of schedTpl) if (!setTpl.has(id)) return false;
      return true;
    },
    {
      message: "ເລືອກແມ່ແບບ ຕ້ອງຄົບກັບ ຕາຕະລາງ (ບໍ່ ຊ້ຳ ບໍ່ ຂາດ)",
      path: ["schedules"],
    },
  )
  .superRefine((data, ctx) => {
    const status = data.pricingStatus ?? "locked";

    if (status === "draft") {
      if (data.price != null && data.price > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ໂໝດ draft — ບໍ່ຕ້ອງປ້ອນລາຄາ (ໃຊ້ຍອດປະມານແທນ)",
          path: ["price"],
        });
      }
      return;
    }

    if (status === "quoted") {
      if (!data.price || data.price < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ໂໝດ quoted — ຕ້ອງປ້ອນລາຄາ",
          path: ["price"],
        });
      }
      return;
    }

    if (!data.price || data.price < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ລາຄາຕ້ອງຫຼາຍກວ່າ 0",
        path: ["price"],
      });
    }
  })
  .superRefine((data, ctx) => {
    if ((data.pricingStatus ?? "locked") !== "locked") return;
    if (data.settlementMode !== "credit") return;

    const rows = data.installments;
    if (!rows || rows.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງກຳນົດແຜນງວດຊຳລະ (ຢ່າງນ້ອຍ 2 ງວດ)",
        path: ["installments"],
      });
      return;
    }

    let finalPrice = data.price;
    if (data.discountType && data.discountValue !== undefined) {
      let discountAmount = 0;
      if (data.discountType === "percentage") {
        discountAmount = Math.round((data.price * data.discountValue) / 100);
      } else {
        discountAmount = Math.round(data.discountValue);
      }
      discountAmount = Math.min(discountAmount, data.price);
      finalPrice = data.price - discountAmount;
      if (finalPrice < 0) finalPrice = 0;
    }

    if (!validateInstallmentPlanTotal(rows, finalPrice)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatInstallmentTotalMismatchMessage(
          sumInstallmentAmounts(rows),
          finalPrice,
        ),
        path: ["installments"],
      });
    }
  });

export type SellPartnerPerClassScheduleDTO = z.infer<
  typeof SellPartnerPerClassScheduleSchema
>;
export type SellPartnerProjectDTO = z.infer<typeof SellPartnerProjectSchema>;

export type SellPartnerProjectResponseDTO = {
  id: string;
  pricingStatus: "draft" | "quoted" | "locked";
  provisionalTotal: number | null;
  originalPrice: number | null;
  finalPrice: number | null;
  discountType: "percentage" | "fixed" | null;
  discountValue: number | null;
  discountAmount: number | null;
  startDate: string;
  expireDate: string;
  partnerLabel: string | null;
  offSiteDistance: "near" | "far";
  notes: string | null;
  classTemplateIds: string[];
  instructorIds: string[];
  classLabels: string[];
  instructorLabels: string[];
  createdAt: string;
  settlementMode?: "receipt" | "credit";
  installmentCount?: number;
  receiptId?: string;
  credit?: {
    invoiceId: string;
    totalAmount: number;
    paidAmount: number;
    remainingAmount: number;
    status: string;
  };
  /** ຈຳນວນ class session ທີ່ຖືກສ້າງອັດຕະໂນມັດຕາມຕາຕະລາງທັງໝົດ */
  scheduledSessionsCount?: number;
};
